import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import State from '../../context';
import loc from '../../utils/loc';

import Pagination from '../Pagination';
import Slider from '../slider';
import { SingleSelection, MultiSelection } from '../Selection';
import Cta from './Cta';

import { BREAKPOINTS } from '../../settings';

const Question = ({
  question,
  total,
  setCurrentQuestionIndex,
  answers,
  setAnswers,
  userAnswer,
  correctAnswer,
  done,
  finalLink,
  skipQuizText,
}) => {
  const humanIndex = question.index + 1;
  const [feedback, setFeedback] = useState('');
  const [questionAnswered, setQuestionAnswered] = useState(false);
  const state = useContext(State);

  // Validate immediately after answer
  useEffect(() => {
    // Only points mode can have validation
    if (state.mode === 'points') {
      if (typeof userAnswer !== 'undefined') {
        if (question.type === 'select' || question.type === 'slider') {
          setQuestionAnswered(true);

          if (userAnswer === correctAnswer) {
            setFeedback(loc.t('correct'));
          } else {
            setFeedback(loc.t('incorrect'));
          }
        }

        if (question.type === 'opinion') {
          // Always correct
          setQuestionAnswered(true);
          setFeedback(loc.t(' '));
        }

        if (question.type === 'multiselect') {
          // Multiselect is special.
          // User has `correctAnswer.length` amount of guesses.
          // One wrong guess and question is disabled, no more guesses possible.

          if (userAnswer.some((answer) => !correctAnswer.includes(answer))) {
            // Has at least one wrong answer, game over
            setQuestionAnswered(true);

            if (userAnswer.some((answer) => correctAnswer.includes(answer))) {
              // Some answers correct, some incorrect
              setFeedback(loc.t('some correct'));
            } else {
              // All incorrect
              setFeedback(loc.t('incorrect'));
            }
          } else if (userAnswer.length === correctAnswer.split(',').length) {
            // All right
            setQuestionAnswered(true);
            setFeedback(loc.t('all correct'));
          } else if (done) {
            setQuestionAnswered(true);
            setFeedback(loc.t('some correct'));
          }
        }
      } else {
        setQuestionAnswered(false);
        setFeedback('');
      }
    } else if (state.mode === 'percents') {
      // Percent mode
      if (done) {
        // Disable fields when all have been answered and results have been shown
        setQuestionAnswered(true);
      } else {
        setQuestionAnswered(false);
      }
    } else if (state.mode === 'single') {
      if (done) {
        setQuestionAnswered(true);
      } else {
        setQuestionAnswered(false);
      }
    }
  }, [userAnswer, done, state.mode, question.type, correctAnswer]);

  /**
   * Handles multiselect changes (checkboxes)
   *
   * @param {Event} event - Checkbox change event
   * @param {any} userAnswer - Current answer

   */
  function handleMultiselectChange(event) {
    const checkbox = event.target;
    const oldValues = [...(answers[question.uuid] || [])].filter(Boolean);
    let newValues = [];

    if (checkbox.checked) {
      newValues = [...oldValues, checkbox.value];
    } else {
      newValues = oldValues.filter((val) => val !== checkbox.value);
    }

    if (newValues.length === 0) {
      newValues = undefined;
    }

    setAnswers({
      ...answers,
      [question.uuid]: newValues,
    });
  }

  /**
   * Handles single selection changes (radio buttons)
   *
   * @param {Event} event - Radio change event
   */
  function handleSingleSelectChange(event) {
    setAnswers({
      ...answers,
      [question.uuid]: event.target.value,
    });
  }

  /**
   * Handles slider changes
   *
   * @param {Event} event - Slider change event
   */
  function handleSliderChange(event) {
    setAnswers({
      ...answers,
      [question.uuid]: parseInt(event.target.value, 10),
    });
  }

  /**
   * Renders option based on type
   *
   * @return {JSX}
   */
  function renderAnswerOptions() {
    if (question.type === 'slider') {
      return (
        <Slider
          settings={question.settings}
          labels={question.labels}
          emojis={question.emojis}
          value={answers[question.uuid]}
          onChange={handleSliderChange}
          color={question.color}
          disabled={questionAnswered}
        />
      );
    } else if (question.type === 'select') {
      const options = question.options.map((option) => {
        let correct;

        if ((state.mode === 'points' || state.mode === 'single') && questionAnswered) {
          correct = correctAnswer === option.uuid;
        }


        return {
          ...option,
          checked: answers[question.uuid] === option.uuid,
          correct,
          explanation: option.explanation
        };
      });

      return (
        <Options>
          <SingleSelection
            options={options}
            onChange={handleSingleSelectChange}
            color={question.color}
            name={question.uuid}
            disabled={questionAnswered}
          />
        </Options>
      );
    } else if (question.type === 'opinion') {
      const options = question.options.map((option) => {
        let correct;

        if ((state.mode === 'points' || state.mode === 'single') && questionAnswered) {
          correct = userAnswer?.includes(option.uuid);
        }


        return {
          ...option,
          checked: correct,
          correct:  correct,
          explanation: option.explanation
        };
      });

      return (
        <Options>
          <SingleSelection
            options={options}
            onChange={handleSingleSelectChange}
            color={question.color}
            name={question.uuid}
            disabled={questionAnswered}
          />
        </Options>
      );
    }else if (question.type === 'multiselect') {
      const options = question.options.map((option) => {
        let correct;

        if (state.mode === 'points' && questionAnswered) {
          correct = correctAnswer.includes(option.uuid);
        }

        return {
          ...option,
          checked:
            Array.isArray(userAnswer) && userAnswer.includes(option.uuid),
          correct,
        };
      });

      return (
        <Options>
          <MultiSelection
            options={options}
            onChange={handleMultiselectChange}
            color={question.color}
            disabled={questionAnswered}
          />
        </Options>
      );
    }

    return null;
  }

  useEffect(() => {
    if (
      questionAnswered &&
      window.innerWidth < 560 &&
      state.settings.paginated
    ) {
      const el = document.querySelector('.js-quiz-feedback');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [questionAnswered]);

  return (
    <Wrapper data-answered={questionAnswered}>
      {question.image ? <Image src={question.image} alt="" /> : null}
      <Content
        className="question-content"
        paginated={state.settings.paginated}
      >
        {state.mode !== 'single' ? <Position>
          {humanIndex}/{total}
        </Position> : null }
        <Title>{question.question}</Title>

        {renderAnswerOptions()}

        {feedback ? (
          <Feedback className="js-quiz-feedback">
            <strong>{feedback}</strong> {question.explanation}
          </Feedback>
        ) : null}

        {state.settings.paginated ? (
          <>
            <Pagination
              index={question.index}
              total={total}
              setCurrentQuestionIndex={setCurrentQuestionIndex}
            />
            {finalLink && skipQuizText && humanIndex < total ? (
              <p>
                <a className="primary" href={finalLink}>
                  {skipQuizText}
                </a>
              </p>
            ) : null}
          </>
        ) : null}

        {questionAnswered && question.cta?.text && question.cta.link ? (
          <Cta {...question.cta} />
        ) : null}
      </Content>
    </Wrapper>
  );
};

Question.propTypes = {
  question: PropTypes.shape({
    index: PropTypes.number,
    uuid: PropTypes.string,
    type: PropTypes.oneOf(['select', 'multiselect', 'slider', 'opinion']),
    question: PropTypes.string,
    image: PropTypes.string,
    color: PropTypes.string,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        uuid: PropTypes.string,
        explanation: PropTypes.string,
      })
    ),
    settings: PropTypes.shape({
      initialValue: PropTypes.number,
      min: PropTypes.number,
      max: PropTypes.number,
    }),
    labels: PropTypes.shape({
      min: PropTypes.string,
      med: PropTypes.string,
      max: PropTypes.string,
    }),
    emojis: PropTypes.object,
    explanation: PropTypes.string,
    cta: PropTypes.shape({
      image: PropTypes.string,
      text: PropTypes.string,
      link: PropTypes.string,
    }),
  }),
  total: PropTypes.number,
  setCurrentQuestionIndex: PropTypes.func,
  answers: PropTypes.object,
  setAnswers: PropTypes.func,
  userAnswer: PropTypes.string | PropTypes.array,
  correctAnswer: PropTypes.string,
  done: PropTypes.bool,
  finalLink: PropTypes.string,
  skipQuizText: PropTypes.string,
};

const Wrapper = styled.div`
  @media (min-width: ${BREAKPOINTS.lg}) {
    padding: 0 40px;
  }
`;

const Content = styled.div`
  padding: 20px ${(props) => (props.paginated ? '16px' : '0px')};
`;

const Position = styled.p`
  color: var(--quiz-primary-color);
  font-size: 17px;
  font-weight: bold;
  line-height: normal;
  margin: 0 0 8px 0;
`;

const Title = styled.h4`
  margin: 0 0 20px 0;
  text-transform: unset;
  white-space: pre-line;
`;

const Feedback = styled.div`
  margin: 1.5rem 0;
`;

const Image = styled.div`
  min-height: 200px;
  height: auto;
  max-width: none;
  width: 100%;
  background-image: ${(props) => `url(${props.src})`};
  background-position: center bottom;
  background-repeat: no-repeat;
  background-size: cover;
  @media (min-width: ${BREAKPOINTS.lg}) {
    min-height: 285px;
  }
`;

const Options = styled.div`
  display: grid;
  gap: 1rem;
`;

export default Question;
